// ===============================
// src/pages/Profilo.jsx
// ===============================
import React, { useEffect, useState, useMemo, useCallback, useId } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { UserRound, Gift, LockKeyhole, FileText, ChevronRight, ArrowLeft, Mail, Phone, MapPin } from "lucide-react";
import CityField from "../components/CityField";
import {
  getProfilo,
  updateProfilo,
  listIndirizzi,
  listAddressOptions,
  createIndirizzo,
  updateIndirizzo,
  deleteIndirizzo,
  changeMyPassword,
  // --- Admin-only (già esistenti)
  adminGetUserProfile,
  adminUpdateUserProfile,
  adminListUserAddresses,
  adminCreateUserAddress,
  adminUpdateUserAddress,
  adminDeleteUserAddress,
  // --- PROMOS (admin + self)
  adminListPromos,
  adminGetPromo,
  adminSetPromoCustomers,
  getMyPromos,
  adminGetUserPromos,
  adminUpsertPromoCustomerValidity,
  adminGetUserPromoRedemptions,
  getClientOrders,
  getMySupplierProfile,
  submitMySupplierProfileRequest,
} from "../Admin/adminApi";
import toast from "react-hot-toast";
import "./css/Profilo.css";
import "../components/profile/CustomerProfile.css";
import CustomerProfileSection from "../components/profile/CustomerProfileSection";
import CustomerAccountActions from "../components/profile/CustomerAccountActions";
import MarketingPreferences from '../components/profile/MarketingPreferences';
import AnalyticsPreferences from '../analytics/AnalyticsPreferences';
import AddressFields, { addressValidationError } from "../components/profile/AddressFields";
import { customerPromoExpiry } from "../utils/customerPromoExpiry";
import { useCustomerPresentation } from "../utils/appMode";
import {
  checkBiometricAvailability,
  hasBiometricLoginCredentials,
  clearBiometricLoginCredentials,
  getBiometricDisplayLabelFromAvailability,
  isBiometricNativeRuntime,
} from "../utils/biometricLogin";
import {
  getMySubscriptions,
  cancelMySubscription,
} from "../api/subscriptionApi";

export default function Profilo({ embedded = false, targetUserId = null }) {
  const profileId = useId();
  const [profileSection, setProfileSection] = useState("profile");
  const [sectionOpened, setSectionOpened] = useState(false);
  const token = localStorage.getItem("token");
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.state?.resetProfileOverview && window.matchMedia("(max-width: 640px)").matches) {
      setSectionOpened(false);
      setProfileSection("profile");
    }
  }, [location.key, location.state]);

  // loading
  const [loading, setLoading] = useState(true);

  const [profileCityConfirmed, setProfileCityConfirmed] = useState(true);
  const [addrCityConfirmed, setAddrCityConfirmed] = useState(true);

  // chi sta navigando (viewer) e chi stiamo mostrando (me)
  const [viewer, setViewer] = useState(null);
  const [me, setMe] = useState({
    id: null,
    name: "",
    last_name: "",
    email: "",
    phone: "",
    citta: "",
    provincia: "",
    role: "",
  });

  const isAdminViewer = String(viewer?.role || "").toLowerCase() === "admin";

  // se sto guardando un altro utente
  const viewingOther = viewer?.id && me?.id && viewer.id !== me.id;
  const customerPresentation = useCustomerPresentation(viewer?.role) && !viewingOther && !embedded;

  const role = String(me.role || "").toLowerCase();
  const canManageAddresses = viewingOther
    ? isAdminViewer
    : role === "user" || role === "admin";

  // indirizzi
  const [addresses, setAddresses] = useState([]);
  const [edit, setEdit] = useState(null); // {id?, label, street, ...}
  const [isEditing, setIsEditing] = useState(false);

  // cambio password (solo quando guardi te stesso)
  const [pwd, setPwd] = useState({ current: "", next: "", next2: "" });
  const [pwdBusy, setPwdBusy] = useState(false);
  // accesso rapido biometrico
  const [bioLoading, setBioLoading] = useState(false);
  const [bioAvailable, setBioAvailable] = useState(false);
  const [bioEnabled, setBioEnabled] = useState(false);
  const [bioLabel, setBioLabel] = useState("Biometria");
  const [bioReason, setBioReason] = useState("");

  // --- Dati personali: dirty check + feedback "Salvato"
  const pickProfilePayload = useCallback((u) => {
    const s = u || {};
    return {
      name: (s.name || "").trim(),
      last_name: (s.last_name || "").trim(),
      phone: (s.phone || "").trim(),
      address: (s.address || "").trim(),
      citta: (s.citta || "").trim(),
      provincia: (s.provincia || "").trim(),
    };
  }, []);

  const [profileBaseline, setProfileBaseline] = useState(null);
  const [profileSavedPulse, setProfileSavedPulse] = useState(false);

  const isProfileDirty = useMemo(() => {
    if (!profileBaseline) return false;
    const cur = pickProfilePayload(me);
    return Object.keys(cur).some((k) => cur[k] !== profileBaseline[k]);
  }, [me, profileBaseline, pickProfilePayload]);

  // --- PROMOS (admin)
  const [promosLoading, setPromosLoading] = useState(false);
  const [promosAll, setPromosAll] = useState([]);
  const [assignedPromos, setAssignedPromos] = useState([]);
  const [promoToAdd, setPromoToAdd] = useState("");

  // --- SELF PROMOS (read-only)
  const [myPromosLoading, setMyPromosLoading] = useState(false);
  const [myPromos, setMyPromos] = useState([]);

  // filtro + espansione card promo
  const [promoFilter, setPromoFilter] = useState("active"); // admin: all; cliente: used
  const [expandedPromos, setExpandedPromos] = useState([]);

  // "Tutte" (admin) e "Utilizzate" (cliente) non sono filtri intercambiabili.
  useEffect(() => {
    setPromoFilter("active");
  }, [customerPresentation]);

  // --- ABBONAMENTI ---
  const [subscriptionsLoading, setSubscriptionsLoading] = useState(false);
  const [subscriptions, setSubscriptions] = useState([]);
  const [subscriptionBusyId, setSubscriptionBusyId] = useState(null);

  // --- PROMO REDEMPTIONS (admin -> other user)
  const [redLoading, setRedLoading] = useState(false);
  const [redPage, setRedPage] = useState(1);
  const [redPageSize] = useState(20);
  const [redTotal, setRedTotal] = useState(0);
  const [redItems, setRedItems] = useState([]);

  // --- ULTIMI ORDINI (admin -> other user)
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersPage, setOrdersPage] = useState(1);
  const [ordersPageSize] = useState(20);
  const [ordersTotal, setOrdersTotal] = useState(0);
  const [ordersItems, setOrdersItems] = useState([]);

  const isFornitore = role === "fornitore";

  const [supplierProfile, setSupplierProfile] = useState({
    fornitore: null,
    pending_request: null,
  });

  const [supplierAlias, setSupplierAlias] = useState("");
  const [supplierFile, setSupplierFile] = useState(null);
  const [supplierPreview, setSupplierPreview] = useState("");
  const [supplierBusy, setSupplierBusy] = useState(false);

  // Richiesta portabilità dati (GDPR Art. 20)
  const [exportStep, setExportStep] = useState("idle"); // 'idle' | 'sending' | 'sent'

  useEffect(() => {
    if (!token || role === "admin" || viewingOther) return;
    const API_BASE = process.env.REACT_APP_API_BASE_URL;
    fetch(`${API_BASE}/api/users/account/data-export-request`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null)
      .then((data) => {
        if (data && data.status === "pending") setExportStep("sent");
      });
  }, [token, role, viewingOther]);

  // Richiesta cancellazione account (GDPR Art. 17) — flusso con approvazione admin
  const [deleteStep, setDeleteStep] = useState("idle"); // 'idle' | 'confirm' | 'sending' | 'sent'

  // Controlla se esiste già una richiesta pending all'avvio
  useEffect(() => {
    if (!token || role === "admin" || viewingOther) return;
    const API_BASE = process.env.REACT_APP_API_BASE_URL;
    fetch(`${API_BASE}/api/users/account/deletion-request`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null)
      .then((data) => {
        if (data && data.status === "pending") {
          setDeleteStep("sent");
        }
      });
  }, [token, role, viewingOther]);

  const reloadSubscriptions = useCallback(async () => {
    if (!token || !me?.id || viewingOther || isFornitore) {
      setSubscriptions([]);
      return;
    }

    try {
      setSubscriptionsLoading(true);
      const data = await getMySubscriptions(token);
      setSubscriptions(
        Array.isArray(data?.subscriptions) ? data.subscriptions : [],
      );
    } catch (e) {
      console.error("reloadSubscriptions failed:", e);
      toast.error(e?.message || "Errore caricamento abbonamenti");
      setSubscriptions([]);
    } finally {
      setSubscriptionsLoading(false);
    }
  }, [token, me?.id, viewingOther, isFornitore]);

  const handleCancelSubscription = useCallback(
    async (subscriptionId) => {
      if (!subscriptionId) return;

      const ok = window.confirm(
        "Vuoi disattivare il rinnovo di questo abbonamento a fine periodo?",
      );

      if (!ok) return;

      try {
        setSubscriptionBusyId(subscriptionId);
        await cancelMySubscription(subscriptionId, token);
        toast.success("Rinnovo disattivato");
        await reloadSubscriptions();
      } catch (e) {
        console.error(e);
        toast.error(e?.message || "Errore cancellazione abbonamento");
      } finally {
        setSubscriptionBusyId(null);
      }
    },
    [token, reloadSubscriptions],
  );

  const DaysChip = ({ days, status }) => {
    if (status === "scheduled")
      return (
        <span className="prf-chip prf-chip--muted">Non ancora attiva</span>
      );
    if (status === "expired")
      return <span className="prf-chip prf-chip--off">Scaduta</span>;
    if (days == null) return <span className="prf-chip">Nessuna Scadenza</span>;
    const cls =
      days <= 2
        ? "prf-chip--danger"
        : days <= 5
        ? "prf-chip--warn"
        : "prf-chip--ok";
    return (
      <span className={`prf-chip ${cls}`} title="Giorni rimanenti">
        {days}g
      </span>
    );
  };

  const [assignedUsage, setAssignedUsage] = useState([]);

  const reloadAdminPromos = useCallback(async () => {
    if (!isAdminViewer || customerPresentation || !me?.id) {
      setPromosAll([]);
      setAssignedPromos([]);
      setAssignedUsage([]);
      setPromosLoading(false);
      return;
    }
    try {
      setPromosLoading(true);
      const base = await adminListPromos(token);
      const details = await Promise.all(
        base.map((p) =>
          adminGetPromo(p.id, token, { forUser: me.id }).catch(() => null),
        ),
      );
      const full = details.filter(Boolean).sort((a, b) => b.id - a.id);
      setPromosAll(full);
      setAssignedPromos(
        full.filter(
          (p) =>
            Array.isArray(p.customer_ids) && p.customer_ids.includes(me.id),
        ),
      );
      try {
        const usageList = await adminGetUserPromos(me.id, token);
        setAssignedUsage(Array.isArray(usageList) ? usageList : []);
      } catch {
        setAssignedUsage([]);
      }
    } finally {
      setPromosLoading(false);
    }
  }, [token, isAdminViewer, customerPresentation, me?.id]);

  const onSetValidityDays = useCallback(
    async (promoId, days, fromDateISO) => {
      try {
        const n = Number(days);
        const body =
          Number.isFinite(n) && n > 0
            ? {
                valid_days: n,
                ...(fromDateISO ? { valid_from: fromDateISO } : {}),
              }
            : {
                valid_days: null,
                ...(fromDateISO ? { valid_from: fromDateISO } : {}),
              };

        await adminUpsertPromoCustomerValidity(promoId, me.id, body, token);
        toast.success("Validità aggiornata");
        await reloadAdminPromos();
      } catch (e) {
        console.error(e);
        toast.error(e?.message || "Errore aggiornamento validità");
      }
    },
    [me.id, token, reloadAdminPromos],
  );

  const fmtRem = (v) => (v == null ? "∞" : String(v));

  // Indirizzi personali (CRUD pieno) + indirizzi hub delle organizzazioni a cui
  // l'utente è associato (sola visualizzazione, non modificabili/eliminabili qui).
  const loadSelfAddresses = useCallback(async () => {
    const personal = await listIndirizzi(token);
    let orgAddresses = [];
    try {
      const options = await listAddressOptions(token);
      orgAddresses = (Array.isArray(options) ? options : [])
        .filter((o) => o.source_type === "organization")
        .map((o) => ({
          ...o,
          id: `org-${o.source_id}`,
          isOrganization: true,
        }));
    } catch (e) {
      console.error("Errore caricamento indirizzi organizzazione:", e);
    }
    return [...(Array.isArray(personal) ? personal : []), ...orgAddresses];
  }, [token]);

  // carico viewer, decido eventuale target (prop > querystring)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);

        const v = await getProfilo(token);
        if (!alive) return;
        setViewer(v);

        const viewerRole = String(v.role || "").toLowerCase();
        const isAdmin = viewerRole === "admin";

        let targetId = null;
        let hasQueryIdParam = false;

        if (targetUserId != null) {
          const n = Number(targetUserId);
          targetId = Number.isFinite(n) && n > 0 ? n : null;
        } else {
          const sp = new URLSearchParams(location.search);
          const rawId = sp.get("userId") ?? sp.get("userid");
          hasQueryIdParam = !!rawId;
          const qsId = Number(rawId);
          targetId = Number.isFinite(qsId) && qsId > 0 ? qsId : null;
        }

        const allowedTarget = !!targetId && isAdmin && targetId !== v.id;

        let userData;
        let addr;

        if (allowedTarget) {
          try {
            [userData, addr] = await Promise.all([
              adminGetUserProfile(targetId, token),
              adminListUserAddresses(targetId, token),
            ]);
          } catch (e) {
            console.error("Admin target load failed:", e);
            if (!embedded) {
              toast.error(
                "Impossibile aprire il profilo richiesto. Mostro il tuo.",
              );
            }
            [userData, addr] = await Promise.all([v, loadSelfAddresses()]);
          }
        } else {
          if (!embedded && hasQueryIdParam && !isAdmin) {
            toast("Parametro 'userid' ignorato (non sei admin).");
          }
          [userData, addr] = await Promise.all([v, loadSelfAddresses()]);
        }

        if (!alive) return;
        setMe(userData);
        setProfileBaseline(pickProfilePayload(userData));
        setProfileCityConfirmed(true);
        setAddresses(Array.isArray(addr) ? addr : []);
        await loadBiometricState();
      } catch (e) {
        console.error(e);
        toast.error("Errore nel caricamento del profilo");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [token, location.search, targetUserId, embedded]);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!isAdminViewer || !viewingOther || !me?.id) return;
      try {
        setRedLoading(true);
        const data = await adminGetUserPromoRedemptions(me.id, token, {
          page: redPage,
          pageSize: redPageSize,
        });
        if (!alive) return;
        setRedTotal(Number(data.total || 0));
        setRedItems(Array.isArray(data.items) ? data.items : []);
      } catch (e) {
        console.error(e);
        toast.error(e?.message || "Errore caricamento utilizzi promo");
        if (!alive) return;
        setRedTotal(0);
        setRedItems([]);
      } finally {
        if (alive) setRedLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [isAdminViewer, viewingOther, me?.id, token, redPage, redPageSize]);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!isAdminViewer || !viewingOther || !me?.id) return;
      try {
        setOrdersLoading(true);

        const data = await getClientOrders(token, me.id, {
          page: ordersPage,
          pageSize: ordersPageSize,
        });

        if (!alive) return;

        // getClientOrders di solito ritorna { total, items } (come clients)
        // ma qui non facciamo i fenomeni: normalizziamo.
        const total = Number(data?.total ?? data?.count ?? 0);
        const items = Array.isArray(data?.items)
          ? data.items
          : Array.isArray(data)
          ? data
          : [];

        setOrdersTotal(Number.isFinite(total) ? total : 0);
        setOrdersItems(items);
      } catch (e) {
        console.error(e);
        toast.error(e?.message || "Errore caricamento ordini");
        if (!alive) return;
        setOrdersTotal(0);
        setOrdersItems([]);
      } finally {
        if (alive) setOrdersLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [isAdminViewer, viewingOther, me?.id, token, ordersPage, ordersPageSize]);

  // PROMO admin
  useEffect(() => {
    reloadAdminPromos();
  }, [reloadAdminPromos]);

  // Abbonamenti
  useEffect(() => {
    if (!me?.id) return;
    reloadSubscriptions();
  }, [me?.id, reloadSubscriptions]);

  // Self promos (read-only)
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!viewer?.id || !me?.id || viewingOther || (isAdminViewer && !customerPresentation)) return;
      try {
        setMyPromosLoading(true);
        const list = await getMyPromos(token);
        if (!alive) return;
        setMyPromos(Array.isArray(list) ? list : []);
      } catch (e) {
        console.error(e);
        toast.error("Errore caricamento promozioni");
      } finally {
        if (alive) setMyPromosLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [token, viewer?.id, me?.id, viewingOther, isAdminViewer, customerPresentation]);

  useEffect(() => {
    let alive = true;

    (async () => {
      if (!token || role !== "fornitore" || viewingOther) return;

      try {
        const data = await getMySupplierProfile(token);
        if (!alive) return;

        setSupplierProfile(data || { fornitore: null, pending_request: null });
        setSupplierAlias(
          data?.pending_request?.requested_nome || data?.fornitore?.nome || "",
        );
        setSupplierPreview(
          data?.pending_request?.requested_cover_url ||
            data?.fornitore?.cover_url ||
            "",
        );
      } catch (e) {
        console.error(e);
        toast.error(e?.message || "Errore caricamento profilo fornitore");
      }
    })();

    return () => {
      alive = false;
    };
  }, [token, role, viewingOther]);

  // Helpers PROMO
  const isActiveNow = (p) => {
    if (!p) return false;
    const now = new Date();
    const startOk = !p.starts_at || new Date(p.starts_at) <= now;
    const endOk = !p.ends_at || new Date(p.ends_at) >= now;
    return !!p.active && startOk && endOk;
  };

  const promoBadge = (p) => {
    if (!p) return "";
    if (p.kind === "percent") return `−${Number(p.value)}%`;
    if (p.kind === "fixed") return `−€${Number(p.value).toFixed(2)}`;
    if (p.kind === "promoqta") return "Quantità";
    if (p.kind === "paniere") return "Paniere";
    return p.kind || "Promo";
  };

  const promoPeriod = (p) => {
    const fmt = (d) =>
      new Date(d).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "2-digit",
      });
    if (p?.starts_at && p?.ends_at)
      return `${fmt(p.starts_at)} → ${fmt(p.ends_at)}`;
    if (p?.starts_at) return `Dal ${fmt(p.starts_at)}`;
    if (p?.ends_at) return `Fino al ${fmt(p.ends_at)}`;
    return "Senza scadenza";
  };

  const usageById = useMemo(() => {
    const m = new Map();
    for (const p of assignedUsage) m.set(p.id, p);
    return m;
  }, [assignedUsage]);

  const getPromoStatus = (p) => {
    const u = usageById.get(p.id) || p;
    const assocStatus = u?.assoc_status || p?.assoc_status || "";
    const rawDays = u?.days_left ?? p?.days_left;
    const daysLeft =
      typeof rawDays === "number" && Number.isFinite(rawDays) ? rawDays : null;

    const isExpired =
      assocStatus === "expired" || (daysLeft != null && daysLeft <= 0);
    const isScheduled = assocStatus === "scheduled";
    const activeNow = isActiveNow(p) && !isExpired;

    return { assocStatus, daysLeft, isExpired, isScheduled, activeNow };
  };

  const filteredAssignedPromos = useMemo(
    () =>
      assignedPromos.filter((p) => {
        const st = getPromoStatus(p);
        if (promoFilter === "active") return st.activeNow;
        if (promoFilter === "expired") return st.isExpired;
        return true; // all
      }),
    [assignedPromos, promoFilter, usageById],
  );

  const assignedDaysFor = (p) => {
    const u = usageById.get(p.id) || p;
    const vf = u?.assoc_valid_from ? new Date(u.assoc_valid_from) : null;
    const vu = u?.assoc_valid_until ? new Date(u.assoc_valid_until) : null;
    if (!vf || !vu) return "";
    const ms = vu - vf;
    if (!Number.isFinite(ms) || ms <= 0) return "";
    const d = Math.max(1, Math.round(ms / 86400000));
    return String(d);
  };

  const fmtMoney = (cents) =>
    (Number(cents || 0) / 100).toLocaleString("it-IT", {
      style: "currency",
      currency: "EUR",
    });

  const fmtDateTime = (d) => {
    if (!d) return "—";
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return "—";
    return dt.toLocaleString("it-IT", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const fmtDateOnly = (v) => {
    if (!v) return "—";
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("it-IT", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  // --- Ordini table helpers (identici a Ordini.jsx)
  function formatCurrency(v) {
    const n = Number(v);
    const isCents = Number.isInteger(n) && n >= 50;
    const eur = isCents ? n / 100 : n;
    return eur.toLocaleString("it-IT", { style: "currency", currency: "EUR" });
  }

  function formatDate(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleDateString("it-IT", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    });
  }

  function statusClass(s) {
    if (!s) return "status-badge";
    const k = String(s).toLowerCase();
    if (k.includes("consegn")) return "status-badge status-badge--ok";
    if (k.includes("lavor")) return "status-badge status-badge--warn";
    if (k.includes("ritir") || k.includes("spedit")) {
      return "status-badge status-badge--info";
    }
    return "status-badge";
  }

  const normalizeOrder = (o) => ({
    id: o?.id ?? null,
    created_at: o?.created_at ?? null,
    status: o?.status ?? "—",
    totale_cents:
      typeof o?.totale_cents === "number"
        ? o.totale_cents
        : Number(o?.totale_cents ?? 0),
    fornitore_nome: (o?.fornitore_nome || "").trim(),
  });

  const onAssignPromo = async () => {
    const pid = Number(promoToAdd);
    if (!pid) return;
    try {
      const pr = promosAll.find((x) => x.id === pid);
      if (!pr) {
        toast.error("Promo non trovata");
        return;
      }
      const current = Array.isArray(pr.customer_ids)
        ? pr.customer_ids.slice()
        : [];
      if (current.includes(me.id)) {
        toast("Promo già associata all'utente");
        setPromoToAdd("");
        return;
      }
      const updated = [...current, me.id];
      await adminSetPromoCustomers(pid, updated, token);
      await reloadAdminPromos();
      setPromoToAdd("");
      toast.success("Promo associata");
    } catch (e) {
      console.error(e);
      toast.error("Associazione promo fallita");
    }
  };

  const onDetachPromo = async (pid) => {
    try {
      const pr = promosAll.find((x) => x.id === pid);
      if (!pr) {
        toast.error("Promo non trovata");
        return;
      }
      const updated = (pr.customer_ids || []).filter((uid) => uid !== me.id);
      await adminSetPromoCustomers(pid, updated, token);
      await reloadAdminPromos();
      toast.success("Promo disassociata");
    } catch (e) {
      console.error(e);
      toast.error("Disassociazione promo fallita");
    }
  };

  const onSubmitSupplierProfileRequest = async (e) => {
    e.preventDefault();

    const trimmedAlias = String(supplierAlias || "").trim();
    if (!trimmedAlias && !supplierFile) {
      toast.error("Inserisci un alias o scegli una nuova immagine");
      return;
    }

    try {
      setSupplierBusy(true);

      const res = await submitMySupplierProfileRequest(
        {
          nome: trimmedAlias || undefined,
          file: supplierFile || undefined,
        },
        token,
      );

      toast.success(res?.message || "Richiesta inviata");

      const refreshed = await getMySupplierProfile(token);
      setSupplierProfile(
        refreshed || { fornitore: null, pending_request: null },
      );
      setSupplierAlias(
        refreshed?.pending_request?.requested_nome ||
          refreshed?.fornitore?.nome ||
          "",
      );
      setSupplierPreview(
        refreshed?.pending_request?.requested_cover_url ||
          refreshed?.fornitore?.cover_url ||
          "",
      );
      setSupplierFile(null);
    } catch (e) {
      console.error(e);
      toast.error(e?.message || "Errore invio richiesta");
    } finally {
      setSupplierBusy(false);
    }
  };

  const loadBiometricState = useCallback(async () => {
    if (!isBiometricNativeRuntime() || viewingOther) {
      setBioAvailable(false);
      setBioEnabled(false);
      setBioLabel("Biometria");
      setBioReason("");
      return;
    }

    try {
      const [availability, enabled] = await Promise.all([
        checkBiometricAvailability(),
        hasBiometricLoginCredentials(),
      ]);

      setBioAvailable(!!availability?.available);
      setBioEnabled(!!enabled);
      setBioLabel(getBiometricDisplayLabelFromAvailability(availability));
      setBioReason(availability?.reason || "");
    } catch (err) {
      setBioAvailable(false);
      setBioEnabled(false);
      setBioLabel("Biometria");
      setBioReason(err?.message || "");
    }
  }, [viewingOther]);

  // --- anagrafica
  const onSaveProfile = async (e) => {
    e.preventDefault();

    if (!profileCityConfirmed) {
      toast.error("Seleziona una città dall’elenco");
      return;
    }
    if (!isProfileDirty) return;

    try {
      const payload = {
        name: me.name,
        last_name: me.last_name,
        phone: me.phone,
        address: me.address,
        citta: me.citta,
        provincia: me.provincia,
      };

      let updated;
      if (viewingOther && isAdminViewer) {
        updated = await adminUpdateUserProfile(me.id, payload, token);
      } else {
        updated = await updateProfilo(payload, token);
      }

      setMe(updated);
      setProfileBaseline(pickProfilePayload(updated));
      toast.success("Profilo aggiornato");

      setProfileSavedPulse(true);
      window.setTimeout(() => setProfileSavedPulse(false), 1500);
    } catch {
      toast.error("Aggiornamento profilo fallito");
    }
  };

  // --- indirizzi
  const startNewAddress = () => {
    setEdit({
      label: "",
      street: "",
      cap: "",
      citta: me.citta || "",
      provincia: me.provincia || "",
      phone: me.phone || "",
      notes: "",
      active: !addresses.some((a) => !a.isOrganization),
    });
    setAddrCityConfirmed(true);
    setIsEditing(true);
  };

  const startEditAddress = (a) => {
    setEdit({ ...a });
    setIsEditing(true);
    setAddrCityConfirmed(true);
  };

  const cancelEdit = () => {
    setEdit(null);
    setIsEditing(false);
  };

  const saveAddress = async (e) => {
    e.preventDefault();
    const validationError = addressValidationError(edit, addrCityConfirmed);
    if (validationError) {
      toast.error(validationError);
      return;
    }
    try {
      let saved;
      if (edit.id) {
        if (viewingOther && isAdminViewer) {
          saved = await adminUpdateUserAddress(me.id, edit.id, edit, token);
        } else {
          saved = await updateIndirizzo(edit.id, edit, token);
        }
        setAddresses((prev) =>
          prev.map((x) => (x.id === saved.id ? saved : x)),
        );
      } else {
        if (viewingOther && isAdminViewer) {
          saved = await adminCreateUserAddress(me.id, edit, token);
        } else {
          saved = await createIndirizzo(edit, token);
        }
        setAddresses((prev) => [saved, ...prev]);
      }
      toast.success("Indirizzo salvato");
      cancelEdit();
    } catch (e2) {
      console.error(e2);
      toast.error("Errore salvataggio indirizzo");
    }
  };

  const removeAddress = async (id) => {
    if (!window.confirm("Eliminare questo indirizzo?")) return;
    try {
      if (viewingOther && isAdminViewer) {
        await adminDeleteUserAddress(me.id, id, token);
      } else {
        await deleteIndirizzo(id, token);
      }
      setAddresses((prev) => prev.filter((x) => x.id !== id));
      toast.success("Indirizzo eliminato");
    } catch {
      toast.error("Errore eliminazione indirizzo");
    }
  };

  const setActive = async (id) => {
    try {
      if (viewingOther && isAdminViewer) {
        await adminUpdateUserAddress(me.id, id, { active: true }, token);
        const fresh = await adminListUserAddresses(me.id, token);
        setAddresses(Array.isArray(fresh) ? fresh : []);
      } else {
        await updateIndirizzo(id, { active: true }, token);
        const fresh = await loadSelfAddresses();
        setAddresses(Array.isArray(fresh) ? fresh : []);
      }
      toast.success("Indirizzo attivo aggiornato");
    } catch {
      toast.error("Errore aggiornamento indirizzo attivo");
    }
  };

  // --- password (solo se sto guardando me stesso)
  const onChangePassword = async (e) => {
    e.preventDefault();
    if (!pwd.current || !pwd.next) {
      toast.error("Compila tutti i campi");
      return;
    }
    if (pwd.next !== pwd.next2) {
      toast.error("Le nuove password non coincidono");
      return;
    }
    if (pwd.next.length < 6) {
      toast.error("La nuova password deve avere almeno 6 caratteri");
      return;
    }
    try {
      setPwdBusy(true);
      const res = await changeMyPassword(pwd.current, pwd.next);
      if (res?.error) throw new Error(res.error);
      toast.success("Password aggiornata");
      setPwd({ current: "", next: "", next2: "" });
    } catch (e2) {
      toast.error(e2?.message || "Cambio password fallito");
    } finally {
      setPwdBusy(false);
    }
  };

  const showOrdersPager = ordersTotal > ordersPageSize;
  const showRedPager = redTotal > redPageSize;

  const displayName =
    [me.name, me.last_name].filter(Boolean).join(" ") || `Utente #${me.id}`;

  const initials = useMemo(() => {
    const parts = [me.name, me.last_name].filter(Boolean);
    if (!parts.length) return "U";
    const first = parts[0].trim()[0] || "";
    const last =
      parts.length > 1 ? parts[parts.length - 1].trim()[0] || "" : "";
    return (first + last).toUpperCase();
  }, [me.name, me.last_name]);

  const roleLabel =
    role === "admin" ? "Admin" : role === "fornitore" ? "Fornitore" : "Cliente";

  if (loading) {
    return (
      <div className="prf-wrap">
        <div className="prf-skel" />
        <div className="prf-skel" />
        <div className="prf-skel" />
      </div>
    );
  }

  const unassignedOptions = promosAll.filter(
    (p) => !(Array.isArray(p.customer_ids) && p.customer_ids.includes(me.id)),
  );

  // Promozioni personali nella presentazione cliente, senza cambiare il ruolo.
  const filteredSelfPromos =
    (!isAdminViewer || customerPresentation) && !viewingOther
      ? myPromos.filter((p) => {
          if (promoFilter === "active") return p.available_for_user === true;
          if (promoFilter === "expired") return p.lost_unused === true;
          return promoFilter === "used" && p.used_in_order === true;
        })
      : [];

  async function disableBiometricLogin() {
    try {
      setBioLoading(true);
      await clearBiometricLoginCredentials();
      setBioEnabled(false);
      toast.success("Accesso rapido disattivato");
    } catch (err) {
      toast.error(err?.message || "Impossibile disattivare l'accesso rapido");
    } finally {
      setBioLoading(false);
    }
  }

  async function refreshBiometricState() {
    try {
      setBioLoading(true);
      await loadBiometricState();
      toast.success("Stato biometria aggiornato");
    } catch (err) {
      toast.error(err?.message || "Impossibile aggiornare lo stato");
    } finally {
      setBioLoading(false);
    }
  }

  async function handleRequestExport() {
    try {
      setExportStep("sending");
      const API_BASE = process.env.REACT_APP_API_BASE_URL;
      const res = await fetch(`${API_BASE}/api/users/account/data-export-request`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Errore durante l'invio della richiesta");
      }
      setExportStep("sent");
    } catch (e) {
      console.error("[requestExport]", e);
      toast.error(e?.message || "Errore durante l'invio della richiesta");
      setExportStep("idle");
    }
  }

  async function handleRequestDeletion() {
    try {
      setDeleteStep("sending");
      const API_BASE = process.env.REACT_APP_API_BASE_URL;
      const res = await fetch(`${API_BASE}/api/users/account/deletion-request`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Errore durante l'invio della richiesta");
      }
      setDeleteStep("sent");
    } catch (e) {
      console.error("[requestDeletion]", e);
      toast.error(e?.message || "Errore durante l'invio della richiesta");
      setDeleteStep("confirm");
    }
  }

  const sectionNavigation = !viewingOther && (!isAdminViewer || customerPresentation) && !isFornitore;
  const profileSections = [
    { id: "profile", label: "Profilo", description: "Dati personali e indirizzi", Icon: UserRound },
    { id: "benefits", label: "Vantaggi", description: "Abbonamenti e promozioni", Icon: Gift },
    { id: "security", label: "Sicurezza", description: "Gestione della password e accesso", Icon: LockKeyhole },
    { id: "privacy", label: "Privacy", description: "Esportazione dati e cancellazione account", Icon: FileText },
  ];

  return (
    <div className="prf-wrap" data-section={sectionNavigation ? profileSection : "all"} data-section-open={sectionOpened}>
      {sectionNavigation && <div className="prf-mobile-heading">
        {sectionOpened ? <button type="button" className="prf-back" onClick={() => setSectionOpened(false)} aria-label="Torna al mio profilo"><ArrowLeft size={22} /></button> : null}
        <h2>{sectionOpened ? profileSections.find(section => section.id === profileSection).label : "Il mio profilo"}</h2>
      </div>}
      {/* HEADER compatto stile app servizi */}
      <div className="prf-header-card">
        <div className="prf-header-avatar">
          <div className="prf-avatar">{initials}</div>
        </div>
        <div className="prf-header-main">
          <div className="prf-header-row">
            <h1 className="prf-header-name">{displayName}</h1>
            {role && (
              <span className={`prf-role-chip prf-role-chip--${role}`}>
                {roleLabel}
              </span>
            )}
            {viewingOther && isAdminViewer && (
              <span className="prf-role-chip prf-role-chip--ghost">
                Vista admin
              </span>
            )}
          </div>
          <div className="prf-header-meta">
            {me.email && <span><Mail size={16} aria-hidden="true" />{me.email}</span>}
            {me.phone && <span><Phone size={16} aria-hidden="true" />{me.phone}</span>}
            {isAdminViewer && viewingOther && me.address && (
              <span><MapPin size={16} aria-hidden="true" />{me.address}</span>
            )}
            {(me.citta || me.provincia) && (
              <span className="prf-header-location">
                <MapPin size={16} aria-hidden="true" /> {me.citta}
                {me.provincia ? ` (${me.provincia})` : ""}
              </span>
            )}
          </div>
        </div>
      </div>

      {isAdminViewer && viewingOther && !embedded && (
        <button className="prf-btn" onClick={() => navigate("/profilo", { replace: true })}>
          Torna al mio profilo
        </button>
      )}

      {sectionNavigation && <nav className="prf-section-nav" aria-label="Sezioni del profilo">
        {profileSections.map(({ id, label, description, Icon }) => <button
          key={id} type="button" className={`prf-nav-item prf-nav-item--${id}`}
          aria-current={profileSection === id ? "true" : undefined}
          onClick={() => { setProfileSection(id); setSectionOpened(true); }}>
          <span className="prf-nav-icon"><Icon size={22} aria-hidden="true" /></span>
          <span className="prf-nav-copy"><strong>{label}</strong><span>{description}</span></span>
          <ChevronRight className="prf-nav-chevron" size={22} aria-hidden="true" />
        </button>)}
      </nav>}

      <div className="prf-cols">
        <div className="prf-col prf-col--left">
          <div className="prf-slot prf-slot--profile">
            <CustomerProfileSection
              title="Dati personali"
              headingClassName="prf-sum--profile"
              actions={
                <>
                  <button
                    type="submit"
                    form={`${profileId}-form`}
                    className="prf-btn prf-btn--primary prf-btn--sm prf-sum-action"
                    disabled={!isProfileDirty || profileSavedPulse}
                    aria-disabled={!isProfileDirty || profileSavedPulse}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    title={!isProfileDirty ? "Nessuna modifica da salvare" : ""}
                  >
                    {profileSavedPulse ? "Salvato" : "Salva"}
                  </button>
                </>
              }
            >
              <div className="prf-body">
                <form
                  id={`${profileId}-form`}
                  onSubmit={onSaveProfile}
                  className="prf-grid prf-personal-grid"
                >
                  <div className="prf-row-inline prf-row-inline--identity">
                    <div className="prf-field">
                      <label htmlFor={`${profileId}-field-1`}>Nome</label>
                      <input
                        id={`${profileId}-field-1`}
                        value={me.name || ""}
                        onChange={(e) =>
                          setMe((m) => ({ ...m, name: e.target.value }))
                        }
                        required
                      />
                    </div>

                    <div className="prf-field">
                      <label htmlFor={`${profileId}-field-2`}>Cognome</label>
                      <input
                        id={`${profileId}-field-2`}
                        value={me.last_name || ""}
                        onChange={(e) =>
                          setMe((m) => ({ ...m, last_name: e.target.value }))
                        }
                        required
                      />
                    </div>
                    <div className="prf-field">
                      <label htmlFor={`${profileId}-cf`}>Codice fiscale (CF)</label>
                      <input id={`${profileId}-cf`} value={me.codice_fiscale || ""} readOnly />
                    </div>
                  </div>

                  <div className="prf-field prf-col-6">
                    <label htmlFor={`${profileId}-field-3`}>Email</label>
                    <input
                      id={`${profileId}-field-3`}
                      value={me.email || ""}
                      disabled
                    />
                  </div>

                  <div className="prf-field prf-col-6">
                    <label htmlFor={`${profileId}-field-4`}>Telefono</label>
                    <input
                      id={`${profileId}-field-4`}
                      value={me.phone || ""}
                      onChange={(e) =>
                        setMe((m) => ({ ...m, phone: e.target.value }))
                      }
                    />
                  </div>

                  <div className="prf-field prf-col-12">
                    <label htmlFor={`${profileId}-address`}>Via e numero civico</label>
                    <input id={`${profileId}-address`} value={me.address || ""}
                      onChange={(e) => setMe((m) => ({ ...m, address: e.target.value }))} />
                  </div>
                  <div className="prf-row-inline prf-row-inline--city">
                    <div className="prf-field">
                      <CityField
                        label="Città"
                        value={me.citta || ""}
                        provinciaValue={me.provincia || ""}
                        capValue=""
                        hardConfirm={true}
                        confirmed={profileCityConfirmed}
                        dropdownPlacement="top"
                        setConfirmed={setProfileCityConfirmed}
                        onChangeCity={(v) => {
                          setMe((m) => ({ ...m, citta: v, provincia: "" }));
                          setProfileCityConfirmed(false);
                        }}
                        onApplyCity={({ name, provincia }) => {
                          setMe((m) => ({ ...m, citta: name, provincia }));
                          setProfileCityConfirmed(true);
                        }}
                        inputClassName="prf-input"
                        showEmptyHint
                      />
                    </div>

                    <div className="prf-field prf-field--province">
                      <label htmlFor={`${profileId}-field-5`}>Provincia</label>
                      <input
                        id={`${profileId}-field-5`}
                        value={me.provincia || ""}
                        readOnly
                        className="prf-input"
                        placeholder="es. MO"
                      />
                    </div>
                  </div>
                </form>
              </div>
            </CustomerProfileSection>

            {isFornitore && !viewingOther && (
              <div className="prf-slot prf-slot--supplier-profile">
                <CustomerProfileSection
                  title="Profilo fornitore"
                  headingClassName=""
                >
                  <div className="prf-body">
                    <form
                      onSubmit={onSubmitSupplierProfileRequest}
                      className="prf-grid"
                    >
                      <div className="prf-col-12 prf-supplier-coverBox">
                        <div className="prf-supplier-coverPreview">
                          {supplierPreview ? (
                            <img
                              src={supplierPreview}
                              alt="Anteprima immagine fornitore"
                              className="prf-supplier-coverImg"
                            />
                          ) : (
                            <div className="prf-supplier-coverPh">
                              Nessuna immagine
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="prf-field prf-col-12">
                        <label htmlFor={`${profileId}-field-6`}>
                          Alias attività
                        </label>
                        <input
                          id={`${profileId}-field-6`}
                          value={supplierAlias}
                          onChange={(e) => setSupplierAlias(e.target.value)}
                          placeholder="Nome attività"
                        />
                      </div>

                      <div className="prf-field prf-col-12">
                        <label htmlFor={`${profileId}-field-7`}>
                          Nuova immagine
                        </label>
                        <input
                          id={`${profileId}-field-7`}
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0] || null;
                            setSupplierFile(file);

                            if (file) {
                              const localUrl = URL.createObjectURL(file);
                              setSupplierPreview(localUrl);
                            }
                          }}
                        />
                      </div>

                      {supplierProfile?.pending_request && (
                        <div className="prf-col-12">
                          <div className="prf-supplier-pendingBox">
                            <strong>Richiesta in attesa di approvazione</strong>
                            <div className="prf-supplier-pendingMeta">
                              Alias richiesto:{" "}
                              {supplierProfile.pending_request.requested_nome ||
                                "—"}
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="prf-actions">
                        <button
                          type="submit"
                          className="prf-btn prf-btn--primary"
                          disabled={supplierBusy}
                        >
                          {supplierBusy
                            ? "Invio..."
                            : "Invia richiesta modifica"}
                        </button>
                      </div>
                    </form>
                  </div>
                </CustomerProfileSection>
              </div>
            )}
          </div>
          {!isFornitore && (
            <div className="prf-slot prf-slot--addr">
              <CustomerProfileSection
                title="Indirizzi ritiro / consegna"
                headingClassName="prf-sum--addr"
                actions={
                  <>
                    {canManageAddresses && !isEditing && (
                      <button
                        type="button"
                        className="prf-btn prf-btn--primary prf-btn--sm prf-sum-plusbtn"
                        title="Nuovo indirizzo"
                        aria-label="Nuovo indirizzo"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          startNewAddress();
                        }}
                      >
                        +
                      </button>
                    )}
                  </>
                }
              >
                <div className="prf-body">
                  {canManageAddresses ? (
                    <>
                      {isEditing && (
                        <form
                          onSubmit={saveAddress}
                          className="prf-grid prf-grid--addr"
                        >
                          <AddressFields edit={edit} setEdit={setEdit} addrCityConfirmed={addrCityConfirmed} setAddrCityConfirmed={setAddrCityConfirmed} idPrefix={profileId} />
                          <div className="prf-actions">
                            <button
                              type="button"
                              className="prf-btn"
                              onClick={cancelEdit}
                            >
                              Annulla
                            </button>
                            <button
                              type="submit"
                              className="prf-btn prf-btn--primary"
                            >
                              Salva
                            </button>
                          </div>
                        </form>
                      )}

                      <ul className="prf-addr">
                        {addresses.map((a) => (
                          <li key={a.id} className="prf-addr-item">
                            <div className="prf-addr-main">
                              <div className="prf-addr-line">
                                <span className="prf-addr-label">
                                  {a.label || "Indirizzo"}
                                </span>

                                {a.isOrganization && (
                                  <span
                                    className="prf-tag prf-tag--info"
                                    title="Indirizzo dell'organizzazione, non modificabile"
                                  >
                                    Organizzazione: {a.organization_name || "—"}
                                  </span>
                                )}

                                {!a.isOrganization && a.active && (
                                  <span
                                    className="prf-checkok"
                                    title="Indirizzo attivo"
                                    aria-label="Indirizzo attivo"
                                  >
                                    ✓
                                  </span>
                                )}

                                <span className="prf-addr-meta-inline">
                                  {a.street}
                                  {a.cap ? `, ${a.cap}` : ""}
                                  {a.citta ? `, ${a.citta}` : ""}
                                  {a.provincia ? ` (${a.provincia})` : ""}
                                </span>

                                {(a.phone || a.notes) && (
                                  <span className="prf-addr-sub-inline">
                                    {a.phone ? `📞 ${a.phone}` : ""}
                                    {a.phone && a.notes ? " • " : ""}
                                    {a.notes || ""}
                                  </span>
                                )}
                              </div>
                            </div>

                            {!a.isOrganization && (
                              <div className="prf-addr-actions prf-addr-actions--compact">
                                {!a.active && (
                                  <button
                                    className="prf-btn prf-btn--ghost prf-btn--sm"
                                    onClick={() => setActive(a.id)}
                                  >
                                    Attiva
                                  </button>
                                )}
                                <button
                                  className="prf-btn prf-btn--primary prf-btn--sm"
                                  onClick={() => startEditAddress(a)}
                                >
                                  Modifica
                                </button>
                                <button
                                  className="prf-btn prf-btn--danger prf-btn--sm"
                                  onClick={() => removeAddress(a.id)}
                                >
                                  Elimina
                                </button>
                              </div>
                            )}
                          </li>
                        ))}
                      </ul>

                      {addresses.length === 0 && (
                        <p className="prf-empty">Nessun indirizzo salvato.</p>
                      )}
                    </>
                  ) : (
                    <p className="prf-muted">
                      Gli indirizzi sono gestibili solo dagli utenti di tipo{" "}
                      <b>Cliente</b> o <b>Admin</b>.
                    </p>
                  )}
                </div>
              </CustomerProfileSection>
            </div>
          )}
          <div className="prf-slot prf-slot--password">
            {(!viewingOther || (isAdminViewer && viewingOther)) && (
              <CustomerProfileSection
                title="Account e sicurezza"
                headingClassName=""
              >
                <div className="prf-body">
                  {!viewingOther ? (
                    <form
                      onSubmit={onChangePassword}
                      className="prf-grid prf-grid--narrow"
                    >
                      <div className="prf-field prf-col-12">
                        <label htmlFor={`${profileId}-field-14`}>
                          Password attuale
                        </label>
                        <input
                          id={`${profileId}-field-14`}
                          type="password"
                          value={pwd.current}
                          onChange={(e) =>
                            setPwd((s) => ({ ...s, current: e.target.value }))
                          }
                          autoComplete="current-password"
                        />
                      </div>

                      <div className="prf-field prf-col-6">
                        <label htmlFor={`${profileId}-field-15`}>
                          Nuova password
                        </label>
                        <input
                          id={`${profileId}-field-15`}
                          type="password"
                          value={pwd.next}
                          onChange={(e) =>
                            setPwd((s) => ({ ...s, next: e.target.value }))
                          }
                          autoComplete="new-password"
                        />
                      </div>

                      <div className="prf-field prf-col-6">
                        <label htmlFor={`${profileId}-field-16`}>
                          Ripeti nuova password
                        </label>
                        <input
                          id={`${profileId}-field-16`}
                          type="password"
                          value={pwd.next2}
                          onChange={(e) =>
                            setPwd((s) => ({ ...s, next2: e.target.value }))
                          }
                          autoComplete="new-password"
                        />
                      </div>

                      <div className="prf-actions">
                        <button
                          type="submit"
                          className="prf-btn prf-btn--primary"
                          disabled={pwdBusy}
                          aria-busy={pwdBusy}
                        >
                          {pwdBusy ? "Salvataggio…" : "Aggiorna password"}
                        </button>
                      </div>
                    </form>
                  ) : (
                    <>
                      <p className="prf-muted" style={{ marginBottom: 8 }}>
                        Vista admin: la password non è modificabile da qui.
                      </p>

                      <form
                        className="prf-grid prf-grid--narrow prf-form--disabled"
                        onSubmit={(e) => e.preventDefault()}
                      >
                        <div className="prf-field prf-col-12">
                          <label htmlFor={`${profileId}-field-17`}>
                            Password attuale
                          </label>
                          <input
                            id={`${profileId}-field-17`}
                            type="password"
                            value=""
                            disabled
                          />
                        </div>

                        <div className="prf-field prf-col-6">
                          <label htmlFor={`${profileId}-field-18`}>
                            Nuova password
                          </label>
                          <input
                            id={`${profileId}-field-18`}
                            type="password"
                            value=""
                            disabled
                          />
                        </div>

                        <div className="prf-field prf-col-6">
                          <label htmlFor={`${profileId}-field-19`}>
                            Ripeti nuova password
                          </label>
                          <input
                            id={`${profileId}-field-19`}
                            type="password"
                            value=""
                            disabled
                          />
                        </div>

                        <div className="prf-actions">
                          <button
                            type="button"
                            className="prf-btn prf-btn--primary"
                            disabled
                          >
                            Disattivato
                          </button>
                        </div>
                      </form>
                    </>
                  )}
                </div>
              </CustomerProfileSection>
            )}
          </div>
          {!viewingOther && (!sectionNavigation || profileSection==='privacy') && <><MarketingPreferences key={token} token={token}/><AnalyticsPreferences/></>}
          <CustomerAccountActions
            activeSection={sectionNavigation ? profileSection : "all"}
            viewingOther={viewingOther}
            role={role}
            deleteStep={deleteStep}
            setDeleteStep={setDeleteStep}
            handleRequestDeletion={handleRequestDeletion}
            exportStep={exportStep}
            handleRequestExport={handleRequestExport}
            bioAvailable={bioAvailable}
            bioReason={bioReason}
            bioLoading={bioLoading}
            refreshBiometricState={refreshBiometricState}
            bioLabel={bioLabel}
            bioEnabled={bioEnabled}
            disableBiometricLogin={disableBiometricLogin}
          />
        </div>

        {!isFornitore && (
          <div className="prf-col prf-col--right">
            <div className="prf-slot prf-slot--subscriptions">
              <CustomerProfileSection
                title="Abbonamenti"
                headingClassName=""
                actions={
                  <>
                    <span
                      className="prf-sum-right"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      title="Abbonamenti attivi o storici"
                    >
                      {subscriptionsLoading ? "···" : subscriptions.length}
                    </span>
                  </>
                }
              >
                <div className="prf-body">
                  {subscriptionsLoading ? (
                    <div className="prf-skel" />
                  ) : subscriptions.length === 0 ? (
                    <div className="prf-sub-empty">
                      <p className="prf-muted">
                        Non hai ancora abbonamenti attivi.
                      </p>

                      <button
                        type="button"
                        className="prf-btn prf-btn--primary"
                        onClick={() => navigate("/abbonamenti")}
                      >
                        Vedi abbonamenti disponibili
                      </button>
                    </div>
                  ) : (
                    <div className="prf-sub-list">
                      {subscriptions.map((sub) => {
                        const canCancel = ["active", "paused"].includes(
                          sub.status,
                        );

                        return (
                          <article className="prf-sub-card" key={sub.id}>
                            <div className="prf-sub-main">
                              <div>
                                <h4>
                                  {sub.plan_name || `Piano #${sub.plan_id}`}
                                </h4>
                                <p>
                                  {sub.supplier_name ||
                                    `Fornitore #${sub.supplier_id}`}{" "}
                                  ·{" "}
                                  {sub.model_name ||
                                    sub.model_code ||
                                    "Abbonamento"}
                                </p>
                              </div>

                              <span
                                className={`prf-tag ${
                                  sub.status === "active"
                                    ? "prf-tag--ok"
                                    : "prf-tag--muted"
                                }`}
                              >
                                {sub.status}
                              </span>
                            </div>

                            <div className="prf-sub-meta">
                              <span>
                                Prezzo:{" "}
                                <strong>
                                  {fmtMoney(sub.price_cents)} / mese
                                </strong>
                              </span>
                              <span>
                                Fine periodo:{" "}
                                <strong>
                                  {fmtDateOnly(sub.current_period_end)}
                                </strong>
                              </span>
                            </div>

                            <div className="prf-sub-actions">
                              {canCancel && (
                                <button
                                  type="button"
                                  className="prf-btn prf-btn--danger prf-btn--sm"
                                  disabled={subscriptionBusyId === sub.id}
                                  onClick={() =>
                                    handleCancelSubscription(sub.id)
                                  }
                                >
                                  {subscriptionBusyId === sub.id
                                    ? "..."
                                    : "Disattiva rinnovo"}
                                </button>
                              )}

                              <button
                                type="button"
                                className="prf-btn prf-btn--primary prf-btn--sm"
                                onClick={() => navigate("/abbonamenti")}
                              >
                                Vedi piani
                              </button>
                            </div>
                          </article>
                        );
                      })}
                    </div>
                  )}
                </div>
              </CustomerProfileSection>
            </div>

            <div className="prf-slot prf-slot--promos">
              {isAdminViewer && !customerPresentation ? (
                <CustomerProfileSection
                  title="Promozioni utente"
                  headingClassName=""
                  actions={
                    <>
                      <span
                        className="prf-sum-filters"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                      >
                        <button
                          type="button"
                          className={`prf-promo-filter-btn ${
                            promoFilter === "active" ? "is-active" : ""
                          }`}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setPromoFilter("active");
                          }}
                        >
                          Attive
                        </button>

                        <button
                          type="button"
                          className={`prf-promo-filter-btn ${
                            promoFilter === "expired" ? "is-active" : ""
                          }`}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setPromoFilter("expired");
                          }}
                        >
                          Scadute
                        </button>

                        <button
                          type="button"
                          className={`prf-promo-filter-btn ${
                            promoFilter === "all" ? "is-active" : ""
                          }`}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setPromoFilter("all");
                          }}
                        >
                          Tutte
                        </button>
                      </span>
                    </>
                  }
                >
                  <div className="prf-body">
                    {promosLoading ? (
                      <div className="prf-skel" />
                    ) : (
                      <>
                        {filteredAssignedPromos.length === 0 ? (
                          <p className="prf-muted">
                            Nessuna promo per il filtro selezionato.
                          </p>
                        ) : (
                          <ul className="prf-promo-list">
                            {filteredAssignedPromos.map((p) => {
                              const st = getPromoStatus(p);
                              const title =
                                p.name || p.code || `Promo #${p.id}`;
                              const customDays = assignedDaysFor(p);
                              const isCollapsed =
                                st.isExpired && !expandedPromos.includes(p.id);
                              const formKey = `v-${p.id}-${st.assocStatus}-${
                                st.daysLeft ?? ""
                              }`;

                              if (isCollapsed) {
                                return (
                                  <li
                                    key={p.id}
                                    className="prf-promo-item prf-promo-item--collapsed"
                                  >
                                    <button
                                      type="button"
                                      className="prf-promo-toggle"
                                      onClick={() =>
                                        setExpandedPromos((prev) =>
                                          prev.includes(p.id)
                                            ? prev.filter((x) => x !== p.id)
                                            : [...prev, p.id],
                                        )
                                      }
                                    >
                                      <div className="prf-promo-collapsed-main">
                                        <span className="prf-promo-title">
                                          {title}
                                        </span>
                                        <span className="prf-chip prf-chip--off">
                                          Scaduta
                                        </span>
                                      </div>
                                      <span className="prf-promo-toggle-icon">
                                        ▾
                                      </span>
                                    </button>

                                    <button
                                      className="prf-btn prf-btn--danger prf-btn--sm"
                                      onClick={() => onDetachPromo(p.id)}
                                      title="Disassocia utente da promo"
                                    >
                                      ✕ Disassocia
                                    </button>
                                  </li>
                                );
                              }

                              return (
                                <li key={p.id} className="prf-promo-item">
                                  <div className="prf-promo-media">
                                    {p.image_url ? (
                                      <img
                                        className="prf-promo-img"
                                        src={p.image_url}
                                        alt={title}
                                      />
                                    ) : (
                                      <div className="prf-promo-img prf-promo-img--ph">
                                        %
                                      </div>
                                    )}
                                  </div>

                                  <div className="prf-promo-content">
                                    <div className="prf-promo-header">
                                      <div className="prf-promo-title-row">
                                        <span className="prf-promo-title">
                                          {title}
                                        </span>

                                        <span
                                          className={`prf-tag ${
                                            st.activeNow
                                              ? "prf-tag--ok"
                                              : "prf-tag--muted"
                                          }`}
                                        >
                                          {promoBadge(p)}
                                        </span>

                                        {p.requires_code && p.code && (
                                          <span className="prf-tag prf-tag--code">
                                            {p.code}
                                          </span>
                                        )}
                                      </div>

                                      <div className="prf-promo-status-row">
                                        <DaysChip
                                          days={st.daysLeft}
                                          status={st.assocStatus}
                                        />
                                      </div>
                                    </div>

                                    <div className="prf-promo-meta-row">
                                      <span>{promoPeriod(p)}</span>
                                      {typeof p.min_subtotal === "number" &&
                                        p.min_subtotal > 0 && (
                                          <span>
                                            · Min €
                                            {Number(p.min_subtotal).toFixed(2)}
                                          </span>
                                        )}
                                      {typeof p.max_discount === "number" &&
                                        p.max_discount > 0 && (
                                          <span>
                                            · Max €
                                            {Number(p.max_discount).toFixed(2)}
                                          </span>
                                        )}
                                    </div>

                                    <div className="prf-promo-meta-row prf-promo-meta-row--secondary">
                                      <span>
                                        Utilizzi rimanenti utente:{" "}
                                        <strong>
                                          {fmtRem(p.remaining_user)}
                                        </strong>
                                      </span>
                                      <span>
                                        Globale:{" "}
                                        <strong>
                                          {fmtRem(p.remaining_global)}
                                        </strong>
                                      </span>
                                    </div>

                                    <div className="prf-promo-footer">
                                      <form
                                        key={formKey}
                                        className="prf-promo-validity-inline"
                                        onSubmit={(e) => {
                                          e.preventDefault();
                                          const daysRaw =
                                            e.currentTarget.elements["days"]
                                              .value;
                                          const days = daysRaw
                                            ? Number(daysRaw)
                                            : null;
                                          onSetValidityDays(p.id, days);
                                        }}
                                      >
                                        <label className="prf-promo-validity-label">
                                          Durata personalizzata (giorni)
                                        </label>

                                        <div className="prf-promo-validity-row">
                                          <input
                                            name="days"
                                            type="number"
                                            min="1"
                                            step="1"
                                            placeholder="es. 3"
                                            className="prf-input prf-input--xs prf-promo-validity-input"
                                            defaultValue={customDays}
                                          />
                                          <button
                                            className="prf-btn prf-btn--tiny"
                                            title="Salva durata personalizzata"
                                          >
                                            ✓
                                          </button>
                                        </div>

                                        <p className="prf-promo-validity-hint">
                                          Vuoto = usa la validità standard
                                          promo.
                                          {customDays && (
                                            <span className="prf-promo-validity-current">
                                              {" "}
                                              Impostata: {customDays}g
                                            </span>
                                          )}
                                        </p>
                                      </form>

                                      <button
                                        className="prf-btn prf-btn--danger prf-btn--sm"
                                        onClick={() => onDetachPromo(p.id)}
                                        title="Disassocia utente da promo"
                                      >
                                        ✕ Disassocia
                                      </button>
                                    </div>
                                  </div>
                                </li>
                              );
                            })}
                          </ul>
                        )}

                        <div className="prf-divider" />
                        <h4 className="prf-subtitle">Associa una promo</h4>

                        {promosAll.length === 0 ? (
                          <p className="prf-muted">
                            Nessuna promo disponibile in archivio.
                          </p>
                        ) : (
                          <form
                            className="prf-grid prf-grid--narrow"
                            onSubmit={(e) => {
                              e.preventDefault();
                              onAssignPromo();
                            }}
                          >
                            <div className="prf-field prf-col-8">
                              <select
                                className="prf-input"
                                value={promoToAdd}
                                onChange={(e) => setPromoToAdd(e.target.value)}
                              >
                                <option value="">— Scegli —</option>
                                {unassignedOptions.map((p) => (
                                  <option key={p.id} value={p.id}>
                                    {(p.name || p.code || `Promo #${p.id}`) +
                                      " · " +
                                      promoBadge(p)}
                                  </option>
                                ))}
                              </select>
                            </div>

                            <div className="prf-actions">
                              <button
                                type="submit"
                                className="prf-btn prf-btn--primary"
                                disabled={!promoToAdd}
                              >
                                Associa
                              </button>
                            </div>
                          </form>
                        )}
                      </>
                    )}
                  </div>
                </CustomerProfileSection>
              ) : !viewingOther ? (
                <CustomerProfileSection
                  title="Le mie promozioni"
                  headingClassName="prf-my-promos-heading"
                  actions={
                    <>
                      <span
                        className="prf-sum-filters"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                      >
                        <button
                          type="button"
                          className={`prf-promo-filter-btn ${
                            promoFilter === "active" ? "is-active" : ""
                          }`}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setPromoFilter("active");
                          }}
                        >
                          Attive
                        </button>

                        <button
                          type="button"
                          className={`prf-promo-filter-btn ${
                            promoFilter === "expired" ? "is-active" : ""
                          }`}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setPromoFilter("expired");
                          }}
                        >
                          Scadute
                        </button>

                        <button
                          type="button"
                          className={`prf-promo-filter-btn ${
                            promoFilter === "used" ? "is-active" : ""
                          }`}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setPromoFilter("used");
                          }}
                        >
                          Utilizzate
                        </button>
                      </span>
                    </>
                  }
                >
                  <div className={`prf-body prf-my-promos ${isBiometricNativeRuntime() ? "prf-my-promos--native" : ""}`}>
                    {myPromosLoading ? (
                      <div className="prf-skel" />
                    ) : (
                      <>
                        {filteredSelfPromos.length === 0 ? (
                          <p className="prf-muted">
                            Nessuna promo per il filtro selezionato.
                          </p>
                        ) : (
                          <ul className="prf-promo-list">
                            {filteredSelfPromos.map((p) => (
                              <li key={p.id} className="prf-promo-item">
                                {p.image_url ? (
                                  <div className="prf-promo-media">
                                    <img
                                      className="prf-promo-img"
                                      src={p.image_url}
                                      alt={p.name || p.code || `Promo #${p.id}`}
                                    />
                                  </div>
                                ) : (
                                  <div className="prf-promo-media">
                                    <div className="prf-promo-img prf-promo-img--ph">
                                      %
                                    </div>
                                  </div>
                                )}

                                <div className="prf-promo-content">
                                  <div className="prf-promo-header">
                                    <div className="prf-promo-title-row">
                                      <span className="prf-promo-title">
                                        {p.name || p.code || `Promo #${p.id}`}
                                      </span>

                                      <span
                                        className={`prf-tag ${
                                          isActiveNow(p)
                                            ? "prf-tag--ok"
                                            : "prf-tag--muted"
                                        }`}
                                      >
                                        {promoBadge(p)}
                                      </span>

                                      {p.requires_code && p.code && (
                                        <span
                                          className="prf-tag prf-tag--code"
                                          title="Codice promozionale"
                                        >
                                          {p.code}
                                        </span>
                                      )}
                                    </div>

                                  </div>

                                  <div className="prf-promo-meta-row">
                                    {customerPromoExpiry(p) && (
                                      <span className="prf-my-promos-expiry">{customerPromoExpiry(p)}</span>
                                    )}
                                    {p.unavailable_reason && p.unavailable_reason !== "scheduled" && p.unavailable_reason !== "expired" && (
                                      <span>{({ expired: "Scaduta", inactive: "Disattivata", global_limit: "Utilizzi globali esauriti", user_limit: "Utilizzi esauriti", first_order: "Riservata al primo ordine", revoked: "Non più assegnata" })[p.unavailable_reason]}</span>
                                    )}
                                    {typeof p.min_subtotal === "number" &&
                                      p.min_subtotal > 0 && (
                                        <span>
                                          · Min €
                                          {Number(p.min_subtotal).toFixed(2)}
                                        </span>
                                      )}
                                    {typeof p.max_discount === "number" &&
                                      p.max_discount > 0 && (
                                        <span>
                                          · Max €
                                          {Number(p.max_discount).toFixed(2)}
                                        </span>
                                      )}
                                  </div>

                                  {typeof p.remaining_user !== "undefined" && (
                                    <div className="prf-promo-meta-row prf-promo-meta-row--secondary">
                                      <span>
                                        <span className="prf-my-promos-label-desktop">Utilizzi rimanenti utente</span><span className="prf-my-promos-label-mobile">Utilizzi rimanenti</span>:{" "}
                                        <strong>
                                          {p.remaining_user == null
                                            ? "∞"
                                            : p.remaining_user}
                                        </strong>
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </li>
                            ))}
                          </ul>
                        )}
                      </>
                    )}
                  </div>
                </CustomerProfileSection>
              ) : (
                <div className="prf-slot-empty" />
              )}
            </div>
          </div>
        )}
      </div>

      {isAdminViewer && viewingOther && (
        <div className="prf-adminWide">
          {/* ULTIMI ORDINI (tabella identica a Ordini.jsx, senza filtri) */}
          <CustomerProfileSection
            title="Ultimi ordini"
            headingClassName=""
            actions={
              <>
                <span
                  className="prf-sum-right"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  title="Totale ordini"
                >
                  {ordersTotal ? `${ordersTotal} tot.` : ""}
                </span>
              </>
            }
          >
            <div className="prf-body">
              {ordersLoading && !ordersItems.length ? (
                <div className="prf-skel" />
              ) : (
                <>
                  <div
                    className="prf-orders-listtable list-table"
                    role="table"
                    aria-label="Elenco ordini"
                  >
                    <div className="list-row list-row--head" role="row">
                      <div
                        className="list-cell list-col-orderid list-col--nr"
                        role="columnheader"
                      >
                        Nr
                      </div>
                      <div
                        className="list-cell list-col--date"
                        role="columnheader"
                      >
                        Data
                      </div>
                      <div
                        className="list-cell list-ta-right list-col--amount"
                        role="columnheader"
                      >
                        Importo
                      </div>
                      <div
                        className="list-cell list-col--status"
                        role="columnheader"
                      >
                        Stato
                      </div>
                      <div
                        className="list-cell list-col--supplier"
                        role="columnheader"
                      >
                        Fornitore
                      </div>
                      <div
                        className="list-cell list-ta-center list-col--detail"
                        role="columnheader"
                      >
                        Dettaglio
                      </div>
                    </div>

                    {ordersItems.map((oRaw) => {
                      const o = normalizeOrder(oRaw);
                      return (
                        <div
                          className="list-row"
                          role="row"
                          key={o.id ?? JSON.stringify(oRaw)}
                        >
                          <div
                            className="list-cell list-col-orderid list-col--nr"
                            role="cell"
                            data-label="Nr"
                          >
                            <span className="list-orderid">{o.id ?? "—"}</span>
                          </div>

                          <div
                            className="list-cell list-col--date"
                            role="cell"
                            data-label="Data"
                          >
                            {fmtDateOnly(o.created_at)}
                          </div>

                          <div
                            className="list-cell list-ta-right list-col--amount"
                            role="cell"
                            data-label="Importo"
                          >
                            {fmtMoney(o.totale_cents)}
                          </div>

                          <div
                            className="list-cell list-col--status"
                            role="cell"
                            data-label="Stato"
                          >
                            <span
                              className={`status-badge ${
                                String(o.status || "")
                                  .toLowerCase()
                                  .includes("consegn")
                                  ? "status-badge--ok"
                                  : ""
                              }`}
                            >
                              {o.status}
                            </span>
                          </div>

                          <div
                            className="list-cell list-col--supplier"
                            role="cell"
                            data-label="Fornitore"
                          >
                            {o.fornitore_nome || "—"}
                          </div>

                          <div
                            className="list-cell list-ta-center list-col--detail"
                            role="cell"
                            data-label="Dettaglio"
                          >
                            <Link
                              className="list-link list-link--icon"
                              to={`/ordini/${o.id}`}
                              state={{
                                from: location.pathname + location.search,
                              }}
                              aria-label={`Vedi ordine ${o.id}`}
                            >
                              <span aria-hidden>👁️</span>
                            </Link>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Pager identico stile Ordini.jsx (numeri + frecce) */}
                  {ordersTotal > ordersPageSize && (
                    <nav
                      className="list-pager prf-orders-pager"
                      aria-label="Pagine ordini"
                    >
                      <button
                        className="list-pagerbtn"
                        onClick={() => setOrdersPage((p) => Math.max(1, p - 1))}
                        aria-label="Pagina precedente"
                        disabled={ordersPage <= 1}
                      >
                        ←
                      </button>

                      <div className="list-pages">
                        {Array.from(
                          {
                            length: Math.max(
                              1,
                              Math.ceil(ordersTotal / ordersPageSize),
                            ),
                          },
                          (_, i) => i + 1,
                        ).map((p) => (
                          <button
                            key={p}
                            className={`list-pagebtn ${
                              p === ordersPage ? "is-active" : ""
                            }`}
                            onClick={() => setOrdersPage(p)}
                          >
                            {p}
                          </button>
                        ))}
                      </div>

                      <button
                        className="list-pagerbtn"
                        onClick={() => {
                          const maxPage = Math.max(
                            1,
                            Math.ceil(ordersTotal / ordersPageSize),
                          );
                          setOrdersPage((p) => Math.min(maxPage, p + 1));
                        }}
                        aria-label="Pagina successiva"
                        disabled={
                          ordersPage >=
                          Math.max(1, Math.ceil(ordersTotal / ordersPageSize))
                        }
                      >
                        →
                      </button>
                    </nav>
                  )}
                </>
              )}
            </div>
          </CustomerProfileSection>

          {/* USO PROMOZIONI */}
          <CustomerProfileSection
            title="Uso promozioni"
            headingClassName=""
            actions={
              <>
                <span
                  className="prf-sum-right"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  title="Totale utilizzi"
                >
                  {redTotal ? `${redTotal} tot.` : ""}
                </span>
              </>
            }
          >
            <div className="prf-body">
              {redLoading ? (
                <div className="prf-skel" />
              ) : redItems.length === 0 ? (
                <p className="prf-muted">Nessun utilizzo promo trovato.</p>
              ) : (
                <>
                  <div
                    className="prf-tableWrap"
                    role="region"
                    aria-label="Tabella utilizzi promo"
                  >
                    <table className="prf-table prf-table--usage" role="table">
                      <thead>
                        <tr>
                          <th>Promo</th>
                          <th>Data</th>
                          <th>Fornitore</th>
                          <th>Ordine</th>
                          <th className="is-right">Sconto</th>
                        </tr>
                      </thead>
                      <tbody>
                        {redItems.map((it) => {
                          const promoLabel =
                            it.promo?.name ||
                            it.promo?.code ||
                            `Promo #${it.promo?.id ?? "—"}`;
                          const supplierName = it.supplier?.name || "—";
                          const orderId = it.order?.id
                            ? `#${it.order.id}`
                            : "—";
                          const discount = fmtMoney(it.discount_cents);

                          return (
                            <tr key={it.id}>
                              <td
                                className="is-strong is-trunc"
                                data-label="Promo"
                                title={promoLabel}
                              >
                                {promoLabel}
                              </td>
                              <td className="is-muted" data-label="Data">
                                {fmtDateOnly(it.used_at)}
                              </td>
                              <td className="is-trunc" data-label="Fornitore" title={supplierName}>
                                {supplierName}
                              </td>
                              <td className="is-mono" data-label="Ordine">{orderId}</td>
                              <td className="is-right is-mono" data-label="Sconto">
                                <span className="prf-moneyNeg">
                                  − {discount}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {showRedPager && (
                    <div className="prf-pagerRow">
                      <button
                        className="prf-btn prf-btn--ghost prf-btn--sm"
                        onClick={() => setRedPage((p) => Math.max(1, p - 1))}
                        disabled={redPage <= 1}
                      >
                        ←
                      </button>
                      <span className="prf-pager-label">Pag. {redPage}</span>
                      <button
                        className="prf-btn prf-btn--ghost prf-btn--sm"
                        onClick={() => {
                          const maxPage = Math.max(
                            1,
                            Math.ceil(redTotal / redPageSize),
                          );
                          setRedPage((p) => Math.min(maxPage, p + 1));
                        }}
                        disabled={
                          redPage >=
                          Math.max(1, Math.ceil(redTotal / redPageSize))
                        }
                      >
                        →
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </CustomerProfileSection>
        </div>
      )}
    </div>
  );
}
